import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ChangeEvent, type Ref, useCallback } from 'react'

import type { ReactTextAreaProps } from '../../types'
import { joinOptionalStringsToSpaceSeparatedString } from '../../util/joinOptionalStringsToSpaceSeparatedString'
import { CssVars } from '../themes'
import type { InputState } from './internal/InternalInput'
import { InternalInputWrapper } from './internal/InternalInputWrapper'
import {
  getInputContainerBorderStyles,
  getInputStyles,
} from './internal/SharedStyles'
import { getStylesForTextType } from './Type'

type Props = {
  /**
   * The value to show in the input. If empty, any provided `placeholder` is shown.
   */
  value: string
  /**
   * Default height of the text area (in lines of text).
   */
  initialNumLines?: number
  /**
   * Callback called when the value in the input changes. As a convenience, the value is provided
   * separately from the change event for use cases that just need the new value.
   */
  onChange: ({
    event,
    value,
  }: {
    event: ChangeEvent<HTMLTextAreaElement>
    value: string
  }) => void
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * Provide `true` to visually hide the input label, while still leaving it present in the DOM
   * for screen readers. Ignored if no `label` is provided.
   */
  hideLabel?: boolean
  /**
   * Provide `true` to enable users to manually resize the TextArea from its initial height.
   */
  isResizable?: boolean
  /**
   * A label to show above the input. If you would like to visually hide it please use
   * `hideLabel` prop to do so. If you do not provide a value here, you must provide your own
   * `<label>` element for the input for accessibility reasons.
   */
  label?: string
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"`.
   */
  errorMessage?: React.ComponentProps<
    typeof InternalInputWrapper
  >['errorMessage']
  /**
   * A React ref to attach to the actual `input` element, e.g. for triggering focus or blur on
   * the input.
   */
  innerRef?: Ref<HTMLTextAreaElement>
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

type DefaultTextAreaProps = Omit<
  ReactTextAreaProps,
  | keyof Props
  | 'disabled'
  | 'onChange'
  | 'css'
  | 'className'
  | 'style'
  | 'resize'
>

/**
 * Text Areas are multi-line text inputs that allow the user to enter long-form content. See
 * the other `*Input` components for collecting more specialized user input.
 *
 * Like all inputs, this should be used inside a `<form>` element. You also may want to use
 * the `getInputState` helper.
 */
export function TextArea({
  errorMessage,
  hideLabel = false,
  initialNumLines = 3,
  innerRef,
  isResizable = false,
  label = '',
  onChange,
  overrideRootStyles,
  state = 'default',
  value,
  ...textAreaProps
}: Props & DefaultTextAreaProps) {
  const onChangeProp = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      onChange({ event, value: event.target.value })
    },
    [onChange]
  )
  return (
    <InternalInputWrapper
      errorMessage={errorMessage}
      hideLabel={hideLabel}
      label={label}
      overrideRootStyles={overrideRootStyles}
      state={state}
    >
      {errorMessageId => (
        <div
          css={[
            getInputContainerBorderStyles(1, state),
            {
              alignItems: 'top',
              display: 'flex',
              height: 'auto',
              minWidth: 192,
              width: '100%',
            },
            overrideRootStyles,
          ]}
        >
          <textarea
            {...textAreaProps}
            ref={innerRef}
            aria-describedby={joinOptionalStringsToSpaceSeparatedString(
              textAreaProps['aria-describedby'],
              errorMessageId
            )}
            aria-invalid={state === 'error'}
            // TODO(eric.lovett): Remove this class name once legacy input styles are removed
            // from web-app.
            className="remove-legacy"
            css={[
              getStylesForTextType({ size: 'S' }),
              {
                flex: 1,

                height: '100%',
                minHeight: `calc(${
                  getStylesForTextType({ size: 'S' }).lineHeight
                } * 2)`,

                paddingBottom: CssVars.size.gutterWidthSm,
                paddingLeft: CssVars.size.gutterWidth,
                paddingRight: CssVars.size.gutterWidth,
                paddingTop: CssVars.size.gutterWidthSm,
              },
              getInputStyles(state),
              isResizable
                ? {
                    resize: 'vertical',
                  }
                : {
                    resize: 'none',
                  },
            ]}
            disabled={state === 'disabled'}
            onChange={onChangeProp}
            rows={initialNumLines}
            value={value}
          />
        </div>
      )}
    </InternalInputWrapper>
  )
}
